/**
 * User Service
 * 
 * Handles user profile management in Firestore
 * - Save/update user profile data (name, email, phone)
 * - Retrieve user profile
 * - Phone number storage for booking lookups
 * 
 * Note: User documents use email as document ID (e.g., users/user@example.com)
 */

import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { initFirebase } from '../firebase';

const { db } = initFirebase();

/**
 * Save or update user profile in Firestore
 * @param {string} email - User email (used as document ID)
 * @param {Object} profileData - User profile data
 * @param {string} profileData.firstName - User's first name
 * @param {string} profileData.lastName - User's last name
 * @param {string} profileData.email - User's email
 * @param {string} profileData.phone - User's phone number (optional)
 * @returns {Promise<void>}
 */
export const saveUserProfile = async (email, profileData) => {
  try {
    const userRef = doc(db, 'users', email.toLowerCase());
    await setDoc(userRef, {
      ...profileData,
      updatedAt: serverTimestamp()
    }, { merge: true });
  } catch (error) {
    console.error('Error saving user profile:', error);
    throw new Error('Failed to save user profile');
  }
};

/**
 * Get user profile from Firestore
 * @param {string} email - User email (used as document ID)
 * @returns {Promise<Object|null>} User profile data or null if not found
 */
export const getUserProfile = async (email) => {
  try {
    const userRef = doc(db, 'users', email.toLowerCase());
    const docSnap = await getDoc(userRef);
    return docSnap.exists() ? docSnap.data() : null;
  } catch (error) {
    console.error('Error getting user profile:', error);
    throw new Error('Failed to get user profile');
  }
};

/**
 * Save user's phone number to profile
 * Used after first booking to enable future booking lookups
 * @param {string} email - User email (used as document ID)
 * @param {string} phone - Phone number (normalized format: +48XXXXXXXXX)
 * @returns {Promise<void>}
 */
export const saveUserPhone = async (email, phone) => {
  try {
    await saveUserProfile(email, { phone });
  } catch (error) {
    console.error('Error saving user phone:', error);
    throw new Error('Failed to save phone number');
  }
};

/**
 * Get user's phone number from profile
 * @param {string} email - User email (used as document ID)
 * @returns {Promise<string|null>} Phone number or null if not found
 */
export const getUserPhone = async (email) => {
  try {
    const profile = await getUserProfile(email);
    return profile?.phone || null;
  } catch (error) {
    console.error('Error getting user phone:', error);
    return null;
  }
};

/**
 * Create initial user profile after registration
 * @param {string} email - User email (used as document ID)
 * @param {Object} userData - Initial user data
 * @param {string} userData.firstName - User's first name
 * @param {string} userData.lastName - User's last name
 * @param {string} userData.email - User's email
 * @param {string} userData.phone - User's phone number (optional)
 * @returns {Promise<void>}
 */
export const createUserProfile = async (email, userData) => {
  try {
    const userRef = doc(db, 'users', email.toLowerCase());
    await setDoc(userRef, {
      ...userData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error creating user profile:', error);
    throw new Error('Failed to create user profile');
  }
};
